import React, { useState, useEffect } from 'react';
import { 
  FaFilter, FaTimes, FaChevronDown, FaChevronUp, FaCalendarAlt, 
  FaHome, FaBuilding, FaMapMarkerAlt, FaDollarSign, FaRulerCombined,
  FaEye, FaCog, FaTruck, FaPercentage, FaEraser
} from 'react-icons/fa';
import { useLanguage } from '../../App';
import './SearchFilters.css';

const SearchFilters = ({
  type = 'projects', // 'projects' or 'units'
  filters = {},
  onFiltersChange,
  onClearFilters,
  isVisible = false,
  onToggle,
  className = ''
}) => {
  const { language } = useLanguage();
  const [localFilters, setLocalFilters] = useState(filters);
  const [expandedSections, setExpandedSections] = useState({
    basic: true,
    price: true,
    dates: false,
    advanced: false
  });

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const handleFilterChange = (key, value) => {
    const newFilters = { ...localFilters, [key]: value };
    setLocalFilters(newFilters);
    if (onFiltersChange) {
      onFiltersChange(newFilters);
    }
  };

  const handleClearAll = () => {
    setLocalFilters({});
    if (onClearFilters) {
      onClearFilters();
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const getActiveFiltersCount = () => {
    return Object.values(localFilters).filter(value => 
      value !== null && value !== undefined && value !== '' && 
      (Array.isArray(value) ? value.length > 0 : true)
    ).length;
  };

  const renderProjectFilters = () => (
    <>
      {/* Basic Filters */}
      <div className="filter-section">
        <div 
          className="filter-section-header"
          onClick={() => toggleSection('basic')}
        >
          <h4 className="filter-section-title">
            <FaBuilding className="section-icon" />
            {language === 'ar' ? 'الفلاتر الأساسية' : 'Basic Filters'}
          </h4>
          {expandedSections.basic ? <FaChevronUp /> : <FaChevronDown />}
        </div>
        
        {expandedSections.basic && (
          <div className="filter-section-content">
            {/* Project Status */}
            <div className="filter-group">
              <label className="filter-label">
                {language === 'ar' ? 'حالة المشروع' : 'Project Status'}
              </label>
              <div className="checkbox-group">
                {[
                  { value: 'pending_approval', label: language === 'ar' ? 'قيد الموافقة' : 'Pending Approval' },
                  { value: 'pre_launch', label: language === 'ar' ? 'ما قبل البيع' : 'Pre-launch' },
                  { value: 'off_plan', label: language === 'ar' ? 'قيد الإنشاء' : 'Off-plan' },
                  { value: 'ready', label: language === 'ar' ? 'جاهز للتسليم' : 'Ready' },
                  { value: 'unavailable', label: language === 'ar' ? 'غير متاح' : 'Unavailable' },
                  { value: 'completed', label: language === 'ar' ? 'مكتمل' : 'Completed' }
                ].map(status => (
                  <label key={status.value} className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={localFilters.status?.includes(status.value) || false}
                      onChange={(e) => {
                        const currentStatus = localFilters.status || [];
                        const newStatus = e.target.checked
                          ? [...currentStatus, status.value]
                          : currentStatus.filter(s => s !== status.value);
                        handleFilterChange('status', newStatus);
                      }}
                    />
                    <span className="checkbox-text">{status.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Developer */}
            <div className="filter-group">
              <label className="filter-label">
                {language === 'ar' ? 'المطور' : 'Developer'}
              </label>
              <input
                type="text"
                className="filter-input"
                placeholder={language === 'ar' ? 'اسم المطور...' : 'Developer name...'}
                value={localFilters.developer || ''}
                onChange={(e) => handleFilterChange('developer', e.target.value)}
              />
            </div>

            {/* City */}
            <div className="filter-group">
              <label className="filter-label">
                <FaMapMarkerAlt className="label-icon" />
                {language === 'ar' ? 'المدينة' : 'City'}
              </label>
              <input
                type="text"
                className="filter-input"
                placeholder={language === 'ar' ? 'اسم المدينة...' : 'City name...'}
                value={localFilters.city || ''}
                onChange={(e) => handleFilterChange('city', e.target.value)}
              />
            </div>
          </div>
        )}
      </div>

      {/* Price Filters */}
      <div className="filter-section">
        <div 
          className="filter-section-header"
          onClick={() => toggleSection('price')}
        >
          <h4 className="filter-section-title">
            <FaDollarSign className="section-icon" />
            {language === 'ar' ? 'نطاق السعر' : 'Price Range'}
          </h4>
          {expandedSections.price ? <FaChevronUp /> : <FaChevronDown />}
        </div>
        
        {expandedSections.price && (
          <div className="filter-section-content">
            <div className="range-inputs">
              <input
                type="number"
                className="filter-input range-input"
                placeholder={language === 'ar' ? 'الحد الأدنى' : 'Min Price'}
                value={localFilters.min_price || ''}
                onChange={(e) => handleFilterChange('min_price', e.target.value)}
              />
              <span className="range-separator">-</span>
              <input
                type="number"
                className="filter-input range-input"
                placeholder={language === 'ar' ? 'الحد الأعلى' : 'Max Price'}
                value={localFilters.max_price || ''}
                onChange={(e) => handleFilterChange('max_price', e.target.value)}
              />
            </div>
          </div>
        )}
      </div>

      {/* Date Filters */}
      <div className="filter-section">
        <div 
          className="filter-section-header"
          onClick={() => toggleSection('dates')}
        >
          <h4 className="filter-section-title">
            <FaCalendarAlt className="section-icon" />
            {language === 'ar' ? 'التواريخ' : 'Dates'}
          </h4>
          {expandedSections.dates ? <FaChevronUp /> : <FaChevronDown />}
        </div>
        
        {expandedSections.dates && (
          <div className="filter-section-content">
            {/* Launch Date */}
            <div className="filter-group">
              <label className="filter-label">
                {language === 'ar' ? 'تاريخ الإطلاق' : 'Launch Date'}
              </label>
              <div className="range-inputs">
                <input
                  type="date"
                  className="filter-input range-input"
                  value={localFilters.launch_date_from || ''}
                  onChange={(e) => handleFilterChange('launch_date_from', e.target.value)}
                />
                <span className="range-separator">-</span>
                <input
                  type="date"
                  className="filter-input range-input"
                  value={localFilters.launch_date_to || ''}
                  onChange={(e) => handleFilterChange('launch_date_to', e.target.value)}
                />
              </div>
            </div>

            {/* Completion Date */}
            <div className="filter-group">
              <label className="filter-label">
                {language === 'ar' ? 'تاريخ الإنجاز' : 'Completion Date'}
              </label>
              <div className="range-inputs">
                <input
                  type="date"
                  className="filter-input range-input"
                  value={localFilters.completion_date_from || ''}
                  onChange={(e) => handleFilterChange('completion_date_from', e.target.value)}
                />
                <span className="range-separator">-</span>
                <input
                  type="date"
                  className="filter-input range-input"
                  value={localFilters.completion_date_to || ''}
                  onChange={(e) => handleFilterChange('completion_date_to', e.target.value)}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Advanced Filters */}
      <div className="filter-section">
        <div 
          className="filter-section-header"
          onClick={() => toggleSection('advanced')}
        >
          <h4 className="filter-section-title">
            <FaCog className="section-icon" />
            {language === 'ar' ? 'فلاتر متقدمة' : 'Advanced Filters'}
          </h4>
          {expandedSections.advanced ? <FaChevronUp /> : <FaChevronDown />}
        </div>
        
        {expandedSections.advanced && (
          <div className="filter-section-content">


            {/* Features */}
            <div className="filter-group">
              <label className="filter-label">
                {language === 'ar' ? 'المميزات' : 'Features'}
              </label>
              <div className="checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={localFilters.has_virtual_tour || false}
                    onChange={(e) => handleFilterChange('has_virtual_tour', e.target.checked)}
                  />
                  <span className="checkbox-text">
                    {language === 'ar' ? 'جولة افتراضية' : 'Virtual Tour'}
                  </span>
                </label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={localFilters.has_live_camera || false}
                    onChange={(e) => handleFilterChange('has_live_camera', e.target.checked)}
                  />
                  <span className="checkbox-text">
                    {language === 'ar' ? 'كاميرا مباشرة' : 'Live Camera'}
                  </span>
                </label>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );

  const renderUnitFilters = () => (
    <>
      {/* Basic Filters */}
      <div className="filter-section">
        <div
          className="filter-section-header"
          onClick={() => toggleSection('basic')}
        >
          <h4 className="filter-section-title">
            <FaHome className="section-icon" />
            {language === 'ar' ? 'الفلاتر الأساسية' : 'Basic Filters'}
          </h4>
          {expandedSections.basic ? <FaChevronUp /> : <FaChevronDown />}
        </div>

        {expandedSections.basic && (
          <div className="filter-section-content">
            {/* Sales Status */}
            <div className="filter-group">
              <label className="filter-label">
                {language === 'ar' ? 'حالة البيع' : 'Sales Status'}
              </label>
              <div className="checkbox-group">
                {[
                  { value: 'Available', label: language === 'ar' ? 'متاح' : 'Available' },
                  { value: 'Not Available', label: language === 'ar' ? 'غير متوفر' : 'Not Available' },
                  { value: 'Reserved', label: language === 'ar' ? 'محجوز' : 'Reserved' },
                  { value: 'Sold', label: language === 'ar' ? 'مباع' : 'Sold' }
                ].map(status => (
                  <label key={status.value} className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={localFilters.sales_status?.includes(status.value) || false}
                      onChange={(e) => {
                        const currentStatus = localFilters.sales_status || [];
                        const newStatus = e.target.checked
                          ? [...currentStatus, status.value]
                          : currentStatus.filter(s => s !== status.value);
                        handleFilterChange('sales_status', newStatus);
                      }}
                    />
                    <span className="checkbox-text">{status.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Property Type */}
            <div className="filter-group">
              <label className="filter-label">
                <FaBuilding className="label-icon" />
                {language === 'ar' ? 'نوع العقار' : 'Property Type'}
              </label>
              <input
                type="text"
                className="filter-input"
                placeholder={language === 'ar' ? 'فيلا، شقة، أرض...' : 'Villa, Apartment, Land...'}
                value={localFilters.property_type || ''}
                onChange={(e) => handleFilterChange('property_type', e.target.value)}
              />
            </div>

            {/* Unit Type (Sub Property Type) */}
            <div className="filter-group">
              <label className="filter-label">
                <FaBuilding className="label-icon" />
                {language === 'ar' ? 'نوع الوحدة' : 'Unit Type'}
              </label>
              <div className="checkbox-group">
                {[
                  { value: 'residential', label: language === 'ar' ? 'السكني' : 'Residential' },
                  { value: 'apartment', label: language === 'ar' ? 'شقة' : 'Apartment' },
                  { value: 'studio', label: language === 'ar' ? 'استوديو' : 'Studio' },
                  { value: 'hotel_apartment', label: language === 'ar' ? 'شقة فندقية' : 'Hotel Apartment' },
                  { value: 'duplex', label: language === 'ar' ? 'دوبلكس' : 'Duplex' },
                  { value: 'loft', label: language === 'ar' ? 'لوفت' : 'Loft' },
                  { value: 'penthouse', label: language === 'ar' ? 'بنتهاوس' : 'Penthouse' },
                  { value: 'townhouse', label: language === 'ar' ? 'تاونهاوس' : 'Townhouse' },
                  { value: 'villa', label: language === 'ar' ? 'فيلا' : 'Villa' },
                  { value: 'independent_house', label: language === 'ar' ? 'منزل مستقل / بيت' : 'Independent House' },
                  { value: 'commercial_plot', label: language === 'ar' ? 'أرض تجارية' : 'Commercial Plot' }
                ].map(unitType => (
                  <label key={unitType.value} className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={localFilters.sub_property_type?.includes(unitType.value) || false}
                      onChange={(e) => {
                        const currentTypes = localFilters.sub_property_type || [];
                        const newTypes = e.target.checked
                          ? [...currentTypes, unitType.value]
                          : currentTypes.filter(t => t !== unitType.value);
                        handleFilterChange('sub_property_type', newTypes);
                      }}
                    />
                    <span className="checkbox-text">{unitType.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Usage Type */}
            <div className="filter-group">
              <label className="filter-label">
                {language === 'ar' ? 'نوع الاستخدام' : 'Usage Type'}
              </label>
              <input
                type="text"
                className="filter-input"
                placeholder={language === 'ar' ? 'سكني، تجاري، صناعي...' : 'Residential, Commercial, Industrial...'}
                value={localFilters.usage_type || ''}
                onChange={(e) => handleFilterChange('usage_type', e.target.value)}
              />
            </div>
          </div>
        )}
      </div>

      {/* Price & Area Filters */}
      <div className="filter-section">
        <div
          className="filter-section-header"
          onClick={() => toggleSection('price')}
        >
          <h4 className="filter-section-title">
            <FaDollarSign className="section-icon" />
            {language === 'ar' ? 'السعر والمساحة' : 'Price & Area'}
          </h4>
          {expandedSections.price ? <FaChevronUp /> : <FaChevronDown />}
        </div>

        {expandedSections.price && (
          <div className="filter-section-content">
            {/* Price Range */}
            <div className="filter-group">
              <label className="filter-label">
                {language === 'ar' ? 'نطاق السعر' : 'Price Range'}
              </label>
              <div className="range-inputs">
                <input
                  type="number"
                  className="filter-input range-input"
                  placeholder={language === 'ar' ? 'الحد الأدنى' : 'Min Price'}
                  value={localFilters.min_price || ''}
                  onChange={(e) => handleFilterChange('min_price', e.target.value)}
                />
                <span className="range-separator">-</span>
                <input
                  type="number"
                  className="filter-input range-input"
                  placeholder={language === 'ar' ? 'الحد الأعلى' : 'Max Price'}
                  value={localFilters.max_price || ''}
                  onChange={(e) => handleFilterChange('max_price', e.target.value)}
                />
              </div>
            </div>

            {/* Area Range */}
            <div className="filter-group">
              <label className="filter-label">
                <FaRulerCombined className="label-icon" />
                {language === 'ar' ? 'نطاق المساحة (م²)' : 'Area Range (m²)'}
              </label>
              <div className="range-inputs">
                <input
                  type="number"
                  className="filter-input range-input"
                  placeholder={language === 'ar' ? 'الحد الأدنى' : 'Min Area'}
                  value={localFilters.min_area || ''}
                  onChange={(e) => handleFilterChange('min_area', e.target.value)}
                />
                <span className="range-separator">-</span>
                <input
                  type="number"
                  className="filter-input range-input"
                  placeholder={language === 'ar' ? 'الحد الأعلى' : 'Max Area'}
                  value={localFilters.max_area || ''}
                  onChange={(e) => handleFilterChange('max_area', e.target.value)}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Advanced Filters */}
      <div className="filter-section">
        <div
          className="filter-section-header"
          onClick={() => toggleSection('advanced')}
        >
          <h4 className="filter-section-title">
            <FaCog className="section-icon" />
            {language === 'ar' ? 'فلاتر متقدمة' : 'Advanced Filters'}
          </h4>
          {expandedSections.advanced ? <FaChevronUp /> : <FaChevronDown />}
        </div>

        {expandedSections.advanced && (
          <div className="filter-section-content">
            {/* View */}
            <div className="filter-group">
              <label className="filter-label">
                <FaEye className="label-icon" />
                {language === 'ar' ? 'الإطلالة' : 'View'}
              </label>
              <input
                type="text"
                className="filter-input"
                placeholder={language === 'ar' ? 'بحر، جبل، حديقة...' : 'Sea, Mountain, Garden...'}
                value={localFilters.view || ''}
                onChange={(e) => handleFilterChange('view', e.target.value)}
              />
            </div>

            {/* Plot Condition */}
            <div className="filter-group">
              <label className="filter-label">
                {language === 'ar' ? 'حالة القطعة' : 'Plot Condition'}
              </label>
              <input
                type="text"
                className="filter-input"
                placeholder={language === 'ar' ? 'ممتاز، جيد، يحتاج صيانة...' : 'Excellent, Good, Needs Maintenance...'}
                value={localFilters.plot_condition || ''}
                onChange={(e) => handleFilterChange('plot_condition', e.target.value)}
              />
            </div>

            {/* Delivery Date */}
            <div className="filter-group">
              <label className="filter-label">
                <FaTruck className="label-icon" />
                {language === 'ar' ? 'تاريخ التسليم' : 'Delivery Date'}
              </label>
              <div className="range-inputs">
                <input
                  type="date"
                  className="filter-input range-input"
                  value={localFilters.delivery_date_from || ''}
                  onChange={(e) => handleFilterChange('delivery_date_from', e.target.value)}
                />
                <span className="range-separator">-</span>
                <input
                  type="date"
                  className="filter-input range-input"
                  value={localFilters.delivery_date_to || ''}
                  onChange={(e) => handleFilterChange('delivery_date_to', e.target.value)}
                />
              </div>
            </div>

            {/* Expected ROI */}
            <div className="filter-group">
              <label className="filter-label">
                <FaPercentage className="label-icon" />
                {language === 'ar' ? 'العائد المتوقع (%)' : 'Expected ROI (%)'}
              </label>
              <div className="range-inputs">
                <input
                  type="number"
                  className="filter-input range-input"
                  placeholder={language === 'ar' ? 'الحد الأدنى' : 'Min ROI'}
                  value={localFilters.min_roi || ''}
                  onChange={(e) => handleFilterChange('min_roi', e.target.value)}
                />
                <span className="range-separator">-</span>
                <input
                  type="number"
                  className="filter-input range-input"
                  placeholder={language === 'ar' ? 'الحد الأعلى' : 'Max ROI'}
                  value={localFilters.max_roi || ''}
                  onChange={(e) => handleFilterChange('max_roi', e.target.value)}
                />
              </div>
            </div>

            {/* Infrastructure & Services */}
            <div className="filter-group">
              <label className="filter-label">
                {language === 'ar' ? 'البنية التحتية' : 'Infrastructure Status'}
              </label>
              <input
                type="text"
                className="filter-input"
                placeholder={language === 'ar' ? 'مكتمل، قيد الإنشاء...' : 'Complete, Under Construction...'}
                value={localFilters.infrastructure_status || ''}
                onChange={(e) => handleFilterChange('infrastructure_status', e.target.value)}
              />
            </div>

            <div className="filter-group">
              <label className="filter-label">
                {language === 'ar' ? 'الخدمات المتاحة' : 'Services Available'}
              </label>
              <input
                type="text"
                className="filter-input"
                placeholder={language === 'ar' ? 'كهرباء، ماء، صرف صحي...' : 'Electricity, Water, Sewage...'}
                value={localFilters.services_available || ''}
                onChange={(e) => handleFilterChange('services_available', e.target.value)}
              />
            </div>
          </div>
        )}
      </div>
    </>
  );

  if (!isVisible) return null;

  return (
    <div className={`search-filters ${language === 'ar' ? 'rtl' : 'ltr'} ${className}`}>
      <div className="filters-header">
        <div className="filters-title">
          <FaFilter className="title-icon" />
          <span>{language === 'ar' ? 'الفلاتر' : 'Filters'}</span>
          {getActiveFiltersCount() > 0 && (
            <span className="active-count">({getActiveFiltersCount()})</span>
          )}
        </div>
        <div className="filters-actions">
          <button 
            className="clear-filters-btn"
            onClick={handleClearAll}
            disabled={getActiveFiltersCount() === 0}
          >
            <FaEraser />
            {language === 'ar' ? 'مسح الكل' : 'Clear All'}
          </button>
          <button className="close-filters-btn" onClick={onToggle}>
            <FaTimes />
          </button>
        </div>
      </div>

      <div className="filters-content">
        {type === 'projects' ? renderProjectFilters() : renderUnitFilters()}
      </div>
    </div>
  );
};

export default SearchFilters;
